import { ToggleButton as MuiToggleButton } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../App.styles';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { HelpASaleItem, useAsyncAction, useTransaction } from '../../../../../hooks';
import { Icon, Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer, ContainerV2 } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { ToggleButton } from '../../../../../stories/molecules/toggleButton/ToggleButton';
import { homeTranslations } from '../../../../../translations';
import { isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { isMultiDayInterval } from '../../../util';
import { HelpASaleGraph } from './helpASaleGraph/HelpASaleGraph';
import { HelpASaleOverview } from './helpASaleOverview/HelpASaleOverview';

type HelpASaleWidgetProps = {
  currentDate: Date;
  isToday: boolean;
  interval: StorePerformanceInterval;
};

export enum HelpASaleViews {
  OVERVIEW = 'Overview',
  GRAPH = 'Graph',
}

export const HelpASaleWidget: FC<HelpASaleWidgetProps> = observer(({ currentDate, isToday, interval }) => {
  const { t } = useTranslation();
  const { helpASale: helpASaleTranslations } = homeTranslations;
  const { fetchHelpASaleData } = useTransaction();

  const [view, setView] = useState(HelpASaleViews.OVERVIEW);
  const [HASDetails, setHASDetails] = useState({
    count: 0,
    countPerVisitor: 0,
    uplift: 0,
    conversionRate: 0,
    sales: 0,
    lastUpdated: new Date(),
  });

  const {
    result: helpASaleData,
    isLoading: helpASaleLoading,
    isError: helpASaleError,
    triggerAction: triggerFetchGetHelpASaleData,
  } = useAsyncAction<HelpASaleItem[]>();

  const fetchData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchHelpASaleData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    const totalHASSales = response.data.sales.reduce((acc, item) => acc + item.helpASaleSales, 0);
    const totalNonHASSales = response.data.sales.reduce((acc, item) => acc + item.nonHelpASaleSales, 0);

    const upliftPercentage = totalNonHASSales != 0 ? (totalHASSales / totalNonHASSales) * 100 : 0;
    const conversionRateValue =
      response.data.customerFacingLookups !== 0
        ? (response.data.numberOfPieces / response.data.customerFacingLookups) * 100
        : 0;
    const HASCountPerVisitorValue =
      response.data.visitors !== 0 ? (response.data.customerFacingLookups / response.data.visitors) * 100 : 0;

    setHASDetails({
      count: response.data.customerFacingLookups,
      countPerVisitor: HASCountPerVisitorValue,
      uplift: upliftPercentage,
      conversionRate: conversionRateValue,
      sales: totalHASSales,
      lastUpdated: response.data.lastUpdated,
    });

    return response.data.sales;
  }, [interval, currentDate]);

  const fetchWidgetData = () => {
    triggerFetchGetHelpASaleData(fetchData);
  };

  useEffect(() => {
    fetchWidgetData();
  }, [currentDate, isToday]);

  const handleChangeView = (event: React.MouseEvent<HTMLElement>, view2: string) => {
    if (view2 !== null) {
      setView(view2 as HelpASaleViews);
    }
  };

  const isNoData = HASDetails.count === 0;

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(helpASaleTranslations.title.key, helpASaleTranslations.title.defaultValue)}
      subHeaderText={t(helpASaleTranslations.subTitle.key, helpASaleTranslations.subTitle.defaultValue)}
      isLoading={helpASaleLoading}
      isError={helpASaleError}
      errorText={t(helpASaleTranslations.error.key, helpASaleTranslations.error.defaultValue)}
      toolTipText={t(helpASaleTranslations.toolTip.key, helpASaleTranslations.toolTip.defaultValue)}
      isNoData={isNoData}
      lastUpdatedDate={HASDetails.lastUpdated}
      headerSideContent={
        <ContainerV2>
          <ToggleButton
            value={view}
            onChange={handleChangeView}
            rightButton={
              <MuiToggleButton value={HelpASaleViews.GRAPH} aria-label="right button">
                <Icon data-testid="right-icon" icon={Icons.TRENDUP} size="small" />
              </MuiToggleButton>
            }
            leftButton={
              <MuiToggleButton value={HelpASaleViews.OVERVIEW} aria-label="left button">
                <Typography type="caption">123</Typography>
              </MuiToggleButton>
            }
          />
        </ContainerV2>
      }
      content={
        <>
          {view === HelpASaleViews.OVERVIEW && (
            <HelpASaleOverview
              uplift={HASDetails.uplift}
              hasSales={HASDetails.sales}
              conversionRate={HASDetails.conversionRate}
              hasCount={HASDetails.count}
              hasCountPerVisitor={HASDetails.countPerVisitor}
            />
          )}

          {view === HelpASaleViews.GRAPH && !isMultiDayInterval(interval) && (
            <ColumnFlexContainer
              data-testid="help-a-sale-widget-noView-wrapper"
              padding={[4]}
              height="100%"
              justifyContent="center"
              alignItems="center"
            >
              <Icon icon={Icons.TRENDUP} size="large" margin={[0, 0, 2, 0]} color="secondaryTextGray" />
              <SecondaryTextTypography type="button" textAlign="center">
                {t(
                  helpASaleTranslations.graphNotAvailableText.key,
                  helpASaleTranslations.graphNotAvailableText.defaultValue,
                )}
              </SecondaryTextTypography>
            </ColumnFlexContainer>
          )}

          {view === HelpASaleViews.GRAPH && isMultiDayInterval(interval) && helpASaleData && (
            <HelpASaleGraph
              uplift={HASDetails.uplift}
              hasSalesItems={helpASaleData}
              filteredPerformanceItems={helpASaleData}
              interval={interval}
            />
          )}
        </>
      }
    />
  );
});
